import React, { useCallback } from 'react';
import {
  type AddDiagnosisRequest,
  type UpdateDiagnosisRequest,
  useAddDiagnosisMutation,
  useRemoveDiagnosisMutation,
  useUpdateDiagnosisMutation,
} from '@features/patients';
import { useSubjectLanguageVariants } from '@features/user';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import type { Diagnosis } from '../types';

import { PatientDiagnosesEditCard } from './PatientDiagnosesEditCard';

interface PatientDiagnosesEditModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  diagnoses: Diagnosis[];
  onDiagnosesUpdated?: () => void;
  /** Source identifier for analytics */
  source: string;
}

export const PatientDiagnosesEditModal: React.FC<PatientDiagnosesEditModalProps> = ({
  open,
  onClose,
  patientId,
  diagnoses,
  onDiagnosesUpdated,
  source,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { singularCap } = useSubjectLanguageVariants();
  const { patientContextBar: contextBarTracking } = useEventTracking();

  const [addDiagnosis, { isLoading: isAdding }] = useAddDiagnosisMutation();
  const [updateDiagnosis] = useUpdateDiagnosisMutation();
  const [removeDiagnosis] = useRemoveDiagnosisMutation();

  const handleAdd = useCallback(
    async (request: AddDiagnosisRequest): Promise<Diagnosis> => {
      try {
        const response = await addDiagnosis({
          patientId,
          addDiagnosisRequest: request,
        }).unwrap();
        onDiagnosesUpdated?.();
        return response.diagnosis as Diagnosis;
      } catch (error) {
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'diagnoses',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [addDiagnosis, patientId, onDiagnosesUpdated, contextBarTracking, source],
  );

  const handleUpdate = useCallback(
    async (diagnosisId: string, patch: UpdateDiagnosisRequest): Promise<void> => {
      try {
        await updateDiagnosis({
          patientId,
          diagnosisId,
          updateDiagnosisRequest: patch,
        }).unwrap();
        onDiagnosesUpdated?.();
      } catch (error) {
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'diagnoses',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [updateDiagnosis, patientId, onDiagnosesUpdated, contextBarTracking, source],
  );

  const handleDelete = useCallback(
    async (diagnosisId: string): Promise<void> => {
      try {
        await removeDiagnosis({ patientId, diagnosisId }).unwrap();
        onDiagnosesUpdated?.();
      } catch (error) {
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'diagnoses',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [removeDiagnosis, patientId, onDiagnosesUpdated, contextBarTracking, source],
  );

  const handleClose = useCallback(() => {
    contextBarTracking.editModalClosed({
      patient_id: patientId,
      data_type: 'diagnoses',
      had_changes: false,
      source,
    });
    onClose();
  }, [onClose, patientId, source, contextBarTracking]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
     maxWidth={"lg"} slotProps={{ paper: {
        sx: {
          height: fullScreen ? '100%' : '80vh',
          maxHeight: fullScreen ? '100%' : '80vh',
          bgcolor: 'background.paper',
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
        },
      } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {`Edit ${singularCap} Diagnoses`}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2 }}>
          <PatientDiagnosesEditCard
            diagnoses={diagnoses}
            onAddDiagnosis={handleAdd}
            onUpdateDiagnosis={handleUpdate}
            onDeleteDiagnosis={handleDelete}
            isLoading={isAdding}
            patientId={patientId}
            source={source}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
